import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, AlertCircle, Database, Link2, DollarSign, ArrowLeftRight, ArrowUpDown } from 'lucide-react';
import { apiService } from '../../services/apiService';

const GAP_TYPE_LABELS = {
  missing_cross_reference: 'Missing Cross-Ref',
  item_not_found: 'Item Not Found',
  description_mismatch: 'Description Mismatch',
  uom_mismatch: 'UOM Mismatch',
  no_contract_pricing: 'No Contract Pricing',
  price_variance: 'Price Variance',
  address_component_mismatch: 'Address Mismatch',
  customer_name_mismatch: 'Customer Name Mismatch',
};

const SEVERITY_STYLES = {
  critical: 'bg-red-500/10 text-red-400',
  warning: 'bg-amber-500/10 text-amber-400',
  info: 'bg-blue-500/10 text-blue-400',
};

function SeverityBadge({ severity }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_STYLES[severity] || 'text-slate-400'}`}>
      {severity}
    </span>
  );
}

export default function ERPGapsTab() {
  const [summary, setSummary] = useState([]);
  const [byCustomer, setByCustomer] = useState([]);
  const [crossRefs, setCrossRefs] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [uomMismatches, setUomMismatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [section, setSection] = useState('summary');
  const [uomSort, setUomSort] = useState({ key: 'Occurrences', dir: 'desc' });

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, custRes, xrefRes, priceRes, uomRes] = await Promise.all([
        apiService.fetchDashboardView('ERPGapSummary'),
        apiService.fetchDashboardView('ERPGapsByCustomer'),
        apiService.fetchDashboardView('MissingCrossReferences'),
        apiService.fetchDashboardView('MissingContractPricing'),
        apiService.fetchDashboardView('UOMMismatches'),
      ]);
      setSummary(sumRes.data || []);
      setByCustomer(custRes.data || []);
      setCrossRefs(xrefRes.data || []);
      setPricing(priceRes.data || []);
      setUomMismatches(uomRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-medium">Failed to load A+ gap data</p>
          <p className="text-red-400/70 text-sm">{error}</p>
        </div>
        <button onClick={loadData} className="ml-auto px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const SECTIONS = [
    { id: 'summary', label: 'Gap Summary', icon: AlertTriangle },
    { id: 'customer', label: 'By Customer', icon: Database },
    { id: 'crossrefs', label: 'Missing Cross-Refs', icon: Link2 },
    { id: 'pricing', label: 'Missing Pricing', icon: DollarSign },
    { id: 'uom', label: 'UOM Mismatches', icon: ArrowLeftRight },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">A+ Data Gaps</h2>
        </div>
        <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Gaps</p>
          <p className="text-2xl font-bold text-white">{summary.reduce((sum, r) => sum + (r.GapCount || 0), 0).toLocaleString()}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Missing Cross-Refs</p>
          <p className="text-2xl font-bold text-amber-400">{crossRefs.length.toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">Unique customer item numbers</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Missing Contract Pricing</p>
          <p className="text-2xl font-bold text-amber-400">{pricing.length.toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">Customer/item combos</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Affected Customers</p>
          <p className="text-2xl font-bold text-white">{new Set(byCustomer.map(r => r.CustomerNumber)).size}</p>
        </div>
      </div>

      {/* Section Selector */}
      <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1 w-fit">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                section === s.id ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Section Content */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        {section === 'summary' && (
          <>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Gap Summary by Type & Severity</h3>
            {summary.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No gaps detected yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Month</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Gap Type</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Severity</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Count</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customers</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Items</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resolved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-2 px-3 text-slate-300">{row.Month}</td>
                        <td className="py-2 px-3 text-slate-300">{GAP_TYPE_LABELS[row.GapType] || row.GapType}</td>
                        <td className="py-2 px-3"><SeverityBadge severity={row.Severity} /></td>
                        <td className="py-2 px-3 text-slate-300">{row.GapCount}</td>
                        <td className="py-2 px-3 text-slate-300">{row.AffectedCustomers}</td>
                        <td className="py-2 px-3 text-slate-300">{row.AffectedItems}</td>
                        <td className="py-2 px-3 text-slate-300">{row.ResolvedByCSR}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {section === 'customer' && (
          <>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Gaps by Customer</h3>
            {byCustomer.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No customer gap data</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer #</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer Name</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Gap Type</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Count</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resolved</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">First Detected</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Detected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCustomer.map((row, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-2 px-3 text-slate-300 font-mono text-xs">{row.CustomerNumber}</td>
                        <td className="py-2 px-3 text-slate-300">{row.CustomerName}</td>
                        <td className="py-2 px-3 text-slate-300">{GAP_TYPE_LABELS[row.GapType] || row.GapType}</td>
                        <td className="py-2 px-3 text-slate-300">{row.GapCount}</td>
                        <td className="py-2 px-3 text-slate-300">{row.ResolvedCount}</td>
                        <td className="py-2 px-3 text-slate-400 text-xs">{row.FirstDetected?.split('T')[0]}</td>
                        <td className="py-2 px-3 text-slate-400 text-xs">{row.LastDetected?.split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {section === 'crossrefs' && (
          <>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Missing IAXRF Cross-References</h3>
            <p className="text-xs text-slate-600 mb-3">Customer item numbers that need to be added to the A+ cross-reference table</p>
            {crossRefs.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No missing cross-references detected</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer #</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer Name</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer Item #</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Matched A+ Item</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Occurrences</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">First Seen</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crossRefs.map((row, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-2 px-3 text-slate-300 font-mono text-xs">{row.CustomerNumber}</td>
                        <td className="py-2 px-3 text-slate-300">{row.CustomerName}</td>
                        <td className="py-2 px-3 text-amber-400 font-mono text-xs">{row.CustomerItemNumber}</td>
                        <td className="py-2 px-3 text-emerald-400 font-mono text-xs">{row.MatchedAplusItem}</td>
                        <td className="py-2 px-3 text-slate-300">{row.Occurrences}</td>
                        <td className="py-2 px-3 text-slate-400 text-xs">{row.FirstSeen?.split('T')[0]}</td>
                        <td className="py-2 px-3 text-slate-400 text-xs">{row.LastSeen?.split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {section === 'pricing' && (
          <>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Missing Contract Pricing</h3>
            <p className="text-xs text-slate-600 mb-3">Customer/item combinations that need contract pricing set up in A+</p>
            {pricing.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No missing contract pricing detected</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer #</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer Name</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">A+ Item #</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Occurrences</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">First Seen</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.map((row, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-2 px-3 text-slate-300 font-mono text-xs">{row.CustomerNumber}</td>
                        <td className="py-2 px-3 text-slate-300">{row.CustomerName}</td>
                        <td className="py-2 px-3 text-amber-400 font-mono text-xs">{row.ItemNumber}</td>
                        <td className="py-2 px-3 text-slate-300">{row.Occurrences}</td>
                        <td className="py-2 px-3 text-slate-400 text-xs">{row.FirstSeen?.split('T')[0]}</td>
                        <td className="py-2 px-3 text-slate-400 text-xs">{row.LastSeen?.split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {section === 'uom' && (() => {
          const uomCols = [
            { key: 'AplusItemNumber', label: 'A+ Item #' },
            { key: 'CustomerUOM', label: 'Customer UOM' },
            { key: 'AplusUOM', label: 'A+ UOM' },
            { key: 'Occurrences', label: 'Occurrences' },
            { key: 'FirstSeen', label: 'First Seen' },
            { key: 'LastSeen', label: 'Last Seen' },
          ];
          const sorted = [...uomMismatches].sort((a, b) => {
            const av = a[uomSort.key] ?? '';
            const bv = b[uomSort.key] ?? '';
            const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
            return uomSort.dir === 'asc' ? cmp : -cmp;
          });
          const toggleSort = (key) => {
            setUomSort(prev => prev.key === key
              ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
              : { key, dir: 'desc' }
            );
          };
          return (
            <>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">UOM Mismatches</h3>
              <p className="text-xs text-slate-600 mb-3">Customer UOMs that don't match A+ UOMs — may need conversion factors or UOM mapping</p>
              {uomMismatches.length === 0 ? (
                <p className="text-slate-500 text-sm py-4 text-center">No UOM mismatches detected</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        {uomCols.map(col => (
                          <th
                            key={col.key}
                            onClick={() => toggleSort(col.key)}
                            className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-300 select-none"
                          >
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              <ArrowUpDown className={`w-3 h-3 ${uomSort.key === col.key ? 'text-amber-400' : 'text-slate-600'}`} />
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((row, i) => (
                        <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-2 px-3 text-slate-300 font-mono text-xs">{row.AplusItemNumber || '—'}</td>
                          <td className="py-2 px-3 text-amber-400 font-mono text-sm font-medium">{row.CustomerUOM || '—'}</td>
                          <td className="py-2 px-3 text-emerald-400 font-mono text-sm font-medium">{row.AplusUOM || '—'}</td>
                          <td className="py-2 px-3 text-slate-300">{row.Occurrences}</td>
                          <td className="py-2 px-3 text-slate-400 text-xs">{row.FirstSeen?.split('T')[0]}</td>
                          <td className="py-2 px-3 text-slate-400 text-xs">{row.LastSeen?.split('T')[0]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
